import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import cytoscape from 'cytoscape';
import dagre from 'cytoscape-dagre';
// @ts-expect-error no type declarations
import elk from 'cytoscape-elk';
import { parseDocument, parseSchema, resolveSchema } from '../lib/viewer/parser';
import { transformToElements, generateStyles } from '../lib/viewer/graph';
import type { ArchiGraphDocument, ResolvedSchema } from '../lib/viewer/types';

cytoscape.use(dagre);
cytoscape.use(elk);

type LoadState =
  | { status: 'empty' }
  | { status: 'loading' }
  | { status: 'loaded'; doc: ArchiGraphDocument; schema: ResolvedSchema }
  | { status: 'error'; message: string };

export function ViewerPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ status: 'empty' });
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdgeIdx, setSelectedEdgeIdx] = useState<number | null>(null);
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [filterLayers, setFilterLayers] = useState<Set<string>>(new Set());
  const [filterKinds, setFilterKinds] = useState<Set<string>>(new Set());
  const cyRef = useRef<cytoscape.Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load from URL params on mount
  useEffect(() => {
    const archigraphUrl = searchParams.get('archigraph');
    const schemaUrl = searchParams.get('schema');
    if (archigraphUrl) {
      loadFromUrls(archigraphUrl, schemaUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFromUrls = async (archigraphUrl: string, schemaUrl: string | null) => {
    setState({ status: 'loading' });
    try {
      const [archRes, schemaRes] = await Promise.all([
        fetch(archigraphUrl),
        schemaUrl ? fetch(schemaUrl) : Promise.resolve(null),
      ]);
      if (!archRes.ok) throw new Error(`Failed to fetch archigraph: ${archRes.status}`);
      const archText = await archRes.text();
      const doc = parseDocument(archText);

      let schema: ResolvedSchema;
      if (schemaRes && schemaRes.ok) {
        const schemaText = await schemaRes.text();
        schema = resolveSchema(parseSchema(schemaText));
      } else {
        schema = inferSchema(doc);
      }

      initFilters(doc);
      setState({ status: 'loaded', doc, schema });
    } catch (e) {
      setState({ status: 'error', message: (e as Error).message });
    }
  };

  const handleFileUpload = async (archFile: File, schemaFile?: File) => {
    setState({ status: 'loading' });
    try {
      const archText = await archFile.text();
      const doc = parseDocument(archText);

      let schema: ResolvedSchema;
      if (schemaFile) {
        const schemaText = await schemaFile.text();
        schema = resolveSchema(parseSchema(schemaText));
      } else {
        schema = inferSchema(doc);
      }

      initFilters(doc);
      setState({ status: 'loaded', doc, schema });
    } catch (e) {
      setState({ status: 'error', message: (e as Error).message });
    }
  };

  const initFilters = (doc: ArchiGraphDocument) => {
    setFilterLayers(new Set(doc.nodes.map(n => n.layer)));
    setFilterKinds(new Set(doc.nodes.map(n => n.kind)));
    setSelectedView(null);
    setSelectedNode(null);
    setSelectedEdgeIdx(null);
  };

  // Build a minimal schema from document data when no schema file is provided
  const inferSchema = (doc: ArchiGraphDocument): ResolvedSchema => {
    const colors = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];
    const layers: ResolvedSchema['layers'] = {};
    const nodeKinds: ResolvedSchema['nodeKinds'] = {};
    const edgeKinds: ResolvedSchema['edgeKinds'] = {};
    let ci = 0;

    for (const node of doc.nodes) {
      if (!layers[node.layer]) {
        layers[node.layer] = { id: node.layer, label: node.layer, order: Object.keys(layers).length, ui: { color: '#666' } };
      }
      if (!nodeKinds[node.kind]) {
        nodeKinds[node.kind] = {
          id: node.kind, label: node.kind, recursive: false, implementable: false,
          ui: { shape: 'ellipse', color: colors[ci++ % colors.length] },
        };
      }
    }
    for (const edge of doc.edges) {
      if (!edgeKinds[edge.kind]) {
        edgeKinds[edge.kind] = { id: edge.kind, label: edge.kind, ui: { color: '#888888' } };
      }
    }
    return { layers, nodeKinds, edgeKinds };
  };

  // Render Cytoscape graph
  useEffect(() => {
    if (state.status !== 'loaded' || !containerRef.current) return;

    const { doc, schema } = state;

    // Apply view filter
    let nodes = doc.nodes;
    let edges = doc.edges;
    if (selectedView) {
      const view = doc.views?.find(v => v.id === selectedView);
      if (view) {
        const includedNodeIds = view.include?.nodes ? new Set(view.include.nodes) : null;
        const includedLayers = view.include?.layers ? new Set(view.include.layers) : null;
        const includedKinds = view.include?.kinds ? new Set(view.include.kinds) : null;
        nodes = nodes.filter(n => {
          if (includedNodeIds && !includedNodeIds.has(n.id)) return false;
          if (includedLayers && !includedLayers.has(n.layer)) return false;
          if (includedKinds && !includedKinds.has(n.kind)) return false;
          return true;
        });
        const visibleIds = new Set(nodes.map(n => n.id));
        edges = edges.filter(e => visibleIds.has(e.from) && visibleIds.has(e.to));
      }
    }

    // Apply layer/kind filters
    nodes = nodes.filter(n => filterLayers.has(n.layer) && filterKinds.has(n.kind));
    const visibleIds = new Set(nodes.map(n => n.id));
    edges = edges.filter(e => visibleIds.has(e.from) && visibleIds.has(e.to));

    const elements = transformToElements(nodes, edges, schema);
    const styles = generateStyles(schema);

    if (cyRef.current) {
      cyRef.current.destroy();
    }

    const cy = cytoscape({
      container: containerRef.current,
      elements: [...elements.nodes, ...elements.edges],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      style: styles as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      layout: {
        name: 'elk',
        elk: {
          algorithm: 'layered',
          'elk.direction': 'DOWN',
          'elk.spacing.nodeNode': '50',
          'elk.layered.spacing.nodeNodeBetweenLayers': '80',
        },
        animate: false,
        fit: true,
        padding: 30,
      } as any,
      minZoom: 0.1,
      maxZoom: 5,
    });

    cy.on('tap', 'node', (e) => {
      const id = e.target.id();
      setSelectedEdgeIdx(null);
      setSelectedNode(id === selectedNode ? null : id);
    });

    cy.on('tap', 'edge', (e) => {
      const edgeId = e.target.id();
      // Parse index from edge id format: edge-{index}-{from}-{to}
      const match = edgeId.match(/^edge-(\d+)-/);
      const idx = match ? parseInt(match[1], 10) : null;
      setSelectedNode(null);
      setSelectedEdgeIdx(idx === selectedEdgeIdx ? null : idx);
    });

    cy.on('tap', (e) => {
      if (e.target === cy) {
        setSelectedNode(null);
        setSelectedEdgeIdx(null);
      }
    });

    // Highlight neighbors on select
    cy.on('select', 'node', (e) => {
      const node = e.target;
      cy.elements().removeClass('faded');
      const neighborhood = node.neighborhood().add(node);
      cy.elements().not(neighborhood).addClass('faded');
      neighborhood.edges().addClass('highlighted');
      neighborhood.nodes().not(node).addClass('neighbor-highlighted');
    });

    cy.on('unselect', 'node', () => {
      cy.elements().removeClass('faded highlighted neighbor-highlighted');
    });

    cyRef.current = cy;

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [state, selectedView, filterLayers, filterKinds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFit = () => cyRef.current?.fit(undefined, 30);

  const handleRelayout = (name: string) => {
    if (!cyRef.current) return;
    const opts: Record<string, unknown> = { name, fit: true, padding: 30, animate: true, animationDuration: 300 };
    if (name === 'elk') {
      opts.elk = { algorithm: 'layered', 'elk.direction': 'DOWN', 'elk.spacing.nodeNode': '50', 'elk.layered.spacing.nodeNodeBetweenLayers': '80' };
    }
    if (name === 'dagre') { opts.rankDir = 'TB'; opts.nodeSep = 50; opts.rankSep = 80; }
    if (name === 'grid') { opts.avoidOverlap = true; }
    if (name === 'circle') { opts.avoidOverlap = true; }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cyRef.current.layout(opts as any).run();
  };

  const toggleFilter = (set: Set<string>, value: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };

  if (state.status === 'empty' || state.status === 'error') {
    return <UploadScreen state={state} onUpload={handleFileUpload} onLoadUrls={loadFromUrls} />;
  }

  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <div className="text-text-light">Loading...</div>
      </div>
    );
  }

  const { doc, schema } = state;
  const allLayers = [...new Set(doc.nodes.map(n => n.layer))].sort();
  const allKinds = [...new Set(doc.nodes.map(n => n.kind))].sort();
  const nodeData = selectedNode ? doc.nodes.find(n => n.id === selectedNode) : null;
  const edgeData = selectedEdgeIdx !== null ? doc.edges[selectedEdgeIdx] : null;
  const connectedEdges = selectedNode
    ? doc.edges.filter(e => e.from === selectedNode || e.to === selectedNode)
    : [];

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Sidebar */}
      <div className="w-64 bg-bg-white border-r border-border flex flex-col shrink-0 overflow-hidden">
        {/* Title */}
        <div className="p-3 border-b border-border">
          <h2 className="text-sm font-medium text-text truncate">{doc.meta?.system || 'ArchiGraph'}</h2>
          <p className="text-xs text-text-lighter mt-0.5">{doc.nodes.length} nodes · {doc.edges.length} edges</p>
        </div>

        {/* Views */}
        {doc.views && doc.views.length > 0 && (
          <div className="p-3 border-b border-border">
            <h3 className="text-xs font-medium text-text-light uppercase tracking-wider mb-2">Views</h3>
            <div className="space-y-1">
              <button
                onClick={() => setSelectedView(null)}
                className={`w-full text-left text-xs px-2 py-1 rounded ${!selectedView ? 'bg-primary/20 text-primary' : 'text-text-light hover:bg-bg-elevated'}`}
              >
                All
              </button>
              {doc.views.map(v => (
                <button
                  key={v.id}
                  onClick={() => setSelectedView(v.id === selectedView ? null : v.id)}
                  className={`w-full text-left text-xs px-2 py-1 rounded truncate ${selectedView === v.id ? 'bg-primary/20 text-primary' : 'text-text-light hover:bg-bg-elevated'}`}
                >
                  {v.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          <div>
            <h3 className="text-xs font-medium text-text-light uppercase tracking-wider mb-2">Layers</h3>
            <div className="space-y-1">
              {allLayers.map(layer => (
                <label key={layer} className="flex items-center gap-2 text-xs text-text-light cursor-pointer hover:text-text">
                  <input
                    type="checkbox"
                    checked={filterLayers.has(layer)}
                    onChange={() => toggleFilter(filterLayers, layer, setFilterLayers)}
                    className="rounded border-border-light accent-primary"
                  />
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: schema.layers[layer]?.ui.color || '#666' }}
                  />
                  {schema.layers[layer]?.label || layer}
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-medium text-text-light uppercase tracking-wider mb-2">Node Kinds</h3>
            <div className="space-y-1">
              {allKinds.map(kind => (
                <label key={kind} className="flex items-center gap-2 text-xs text-text-light cursor-pointer hover:text-text">
                  <input
                    type="checkbox"
                    checked={filterKinds.has(kind)}
                    onChange={() => toggleFilter(filterKinds, kind, setFilterKinds)}
                    className="rounded border-border-light accent-primary"
                  />
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: schema.nodeKinds[kind]?.ui.color || '#666' }}
                  />
                  {schema.nodeKinds[kind]?.label || kind}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Layout controls */}
        <div className="p-3 border-t border-border space-y-2">
          <div className="flex gap-1">
            {['elk', 'dagre', 'grid', 'circle'].map(layout => (
              <button
                key={layout}
                onClick={() => handleRelayout(layout)}
                className="flex-1 text-xs px-2 py-1.5 bg-bg-elevated text-text-light rounded hover:bg-border hover:text-text transition-colors"
              >
                {layout}
              </button>
            ))}
          </div>
          <button
            onClick={handleFit}
            className="w-full text-xs px-2 py-1.5 bg-bg-elevated text-text-light rounded hover:bg-border hover:text-text transition-colors"
          >
            Fit to screen
          </button>
          <button
            onClick={() => { setState({ status: 'empty' }); setSelectedNode(null); }}
            className="w-full text-xs px-2 py-1.5 text-text-lighter hover:text-error transition-colors"
          >
            Load different files
          </button>
        </div>
      </div>

      {/* Graph */}
      <div className="flex-1 relative">
        <div ref={containerRef} className="w-full h-full bg-bg" />

        {/* Node inspector */}
        {nodeData && (
          <InspectorPanel
            title={nodeData.name || nodeData.id}
            onClose={() => setSelectedNode(null)}
          >
            <Field label="ID" value={nodeData.id} />
            <Field label="Kind" value={nodeData.kind} color={schema.nodeKinds[nodeData.kind]?.ui.color} />
            <Field label="Layer" value={nodeData.layer} />
            {nodeData.type && <Field label="Type" value={nodeData.type} />}
            {typeof nodeData.uuid === 'string' && <Field label="UUID" value={nodeData.uuid} />}
            {nodeData.tags && nodeData.tags.length > 0 && (
              <TagsField label="Tags" values={nodeData.tags} />
            )}
            {nodeData.implementable !== undefined && (
              <Field label="Implementable" value={String(nodeData.implementable)} />
            )}
            <XFields data={nodeData.x} />
            {connectedEdges.length > 0 && (
              <div className="pt-1">
                <span className="text-xs text-text-lighter uppercase tracking-wider">Connections ({connectedEdges.length})</span>
                <div className="mt-1 space-y-1">
                  {connectedEdges.map((e, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        const idx = doc.edges.indexOf(e);
                        setSelectedNode(null);
                        setSelectedEdgeIdx(idx);
                      }}
                      className="w-full text-left text-xs text-text-light hover:text-text hover:bg-bg-elevated rounded px-1 py-0.5 transition-colors"
                    >
                      <span className="text-text-lighter">{e.kind}</span>{' '}
                      {e.from === selectedNode ? `→ ${e.to}` : `← ${e.from}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </InspectorPanel>
        )}

        {/* Edge inspector */}
        {edgeData && (
          <InspectorPanel
            title={`${edgeData.kind}: ${edgeData.from} → ${edgeData.to}`}
            onClose={() => setSelectedEdgeIdx(null)}
          >
            <Field label="Kind" value={edgeData.kind} color={schema.edgeKinds[edgeData.kind]?.ui.color} />
            <Field label="From" value={edgeData.from} />
            <Field label="To" value={edgeData.to} />
            {edgeData.layer && <Field label="Layer" value={edgeData.layer} />}
            {edgeData.type && <Field label="Type" value={edgeData.type} />}
            {typeof edgeData.uuid === 'string' && <Field label="UUID" value={edgeData.uuid} />}
            {edgeData.tags && edgeData.tags.length > 0 && (
              <TagsField label="Tags" values={edgeData.tags} />
            )}
            <XFields data={edgeData.x} />
          </InspectorPanel>
        )}
      </div>
    </div>
  );
}

function InspectorPanel({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="absolute top-4 right-4 w-80 bg-bg-white border border-border rounded-lg shadow-xl overflow-hidden max-h-[calc(100vh-7rem)]">
      <div className="p-3 border-b border-border flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-text truncate">{title}</h3>
        <button onClick={onClose} className="text-text-lighter hover:text-text text-xs shrink-0">✕</button>
      </div>
      <div className="p-3 space-y-2 overflow-y-auto max-h-[calc(100vh-10rem)]">
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <span className="text-xs text-text-lighter">{label}</span>
      <div className="text-sm text-text flex items-center gap-1.5 break-all">
        {color && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />}
        {value}
      </div>
    </div>
  );
}

function TagsField({ label, values }: { label: string; values: string[] }) {
  return (
    <div>
      <span className="text-xs text-text-lighter">{label}</span>
      <div className="flex flex-wrap gap-1 mt-0.5">
        {values.map(t => (
          <span key={t} className="text-xs bg-bg-elevated px-1.5 py-0.5 rounded text-text-light">{t}</span>
        ))}
      </div>
    </div>
  );
}

function XFields({ data }: { data?: Record<string, unknown> }) {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <div className="pt-1">
      <span className="text-xs text-text-lighter uppercase tracking-wider">Extension Fields</span>
      <div className="mt-1 space-y-1.5">
        {Object.entries(data).map(([key, value]) => (
          <XFieldValue key={key} fieldKey={key} value={value} depth={0} />
        ))}
      </div>
    </div>
  );
}

function XFieldValue({ fieldKey, value, depth }: { fieldKey: string; value: unknown; depth: number }) {
  if (value === null || value === undefined) return null;

  // Nested object
  if (typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    return (
      <div style={{ paddingLeft: depth > 0 ? 8 : 0 }}>
        <span className="text-xs text-primary/80 font-medium">{fieldKey}</span>
        <div className="mt-0.5 space-y-1 border-l border-border-light pl-2 ml-0.5">
          {entries.map(([k, v]) => (
            <XFieldValue key={k} fieldKey={k} value={v} depth={depth + 1} />
          ))}
        </div>
      </div>
    );
  }

  // Array
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    // Simple string/number array: show as tags
    if (value.every(v => typeof v === 'string' || typeof v === 'number')) {
      return (
        <div style={{ paddingLeft: depth > 0 ? 8 : 0 }}>
          <span className="text-xs text-text-lighter">{fieldKey}</span>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {value.map((v, i) => (
              <span key={i} className="text-xs bg-bg-elevated px-1.5 py-0.5 rounded text-text-light">{String(v)}</span>
            ))}
          </div>
        </div>
      );
    }
    // Complex array: show as syntax-highlighted JSON
    return (
      <div style={{ paddingLeft: depth > 0 ? 8 : 0 }}>
        <span className="text-xs text-text-lighter">{fieldKey}</span>
        <JsonBlock value={value} />
      </div>
    );
  }

  // Check if string value is parseable JSON
  const str = String(value);
  if (typeof value === 'string') {
    const trimmed = str.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return (
          <div style={{ paddingLeft: depth > 0 ? 8 : 0 }}>
            <span className="text-xs text-text-lighter">{fieldKey}</span>
            <JsonBlock value={parsed} />
          </div>
        );
      } catch {
        // not valid JSON, fall through
      }
    }
  }

  // Long string: show as block
  if (str.length > 80 || str.includes('\n')) {
    return (
      <div style={{ paddingLeft: depth > 0 ? 8 : 0 }}>
        <span className="text-xs text-text-lighter">{fieldKey}</span>
        <pre className="text-xs text-text-light bg-bg-elevated rounded p-1.5 mt-0.5 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
          {str}
        </pre>
      </div>
    );
  }

  // Simple scalar
  return (
    <div style={{ paddingLeft: depth > 0 ? 8 : 0 }}>
      <span className="text-xs text-text-lighter">{fieldKey}</span>
      <div className="text-xs text-text-light break-all">{str}</div>
    </div>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  const json = JSON.stringify(value, null, 2);
  return (
    <pre className="text-xs bg-bg-elevated rounded p-2 mt-0.5 overflow-x-auto whitespace-pre break-all leading-relaxed font-mono">
      {highlightJson(json)}
    </pre>
  );
}

function highlightJson(json: string): React.ReactNode[] {
  // Tokenize JSON string into colored spans
  const tokens: React.ReactNode[] = [];
  // Match JSON tokens: strings, numbers, booleans, null, braces/brackets, colons, commas
  const regex = /("(?:[^"\\]|\\.)*")\s*:|("(?:[^"\\]|\\.)*")|([-+]?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(\btrue\b|\bfalse\b)|(\bnull\b)|([\[\]{}])|([,:])|([ \t\n\r]+)/g;
  let match: RegExpExecArray | null;
  let lastIndex = 0;

  while ((match = regex.exec(json)) !== null) {
    // Add any skipped characters
    if (match.index > lastIndex) {
      tokens.push(json.slice(lastIndex, match.index));
    }
    lastIndex = regex.lastIndex;

    const i = tokens.length;

    if (match[1] !== undefined) {
      // Key (string followed by colon) — render key + colon
      const key = match[1];
      tokens.push(
        <span key={`k${i}`} className="text-purple-400">{key}</span>
      );
      // The colon and space after are part of the match but not captured separately here
      const remainder = match[0].slice(key.length);
      if (remainder) tokens.push(<span key={`c${i}`} className="text-text-lighter">{remainder}</span>);
    } else if (match[2] !== undefined) {
      // String value
      tokens.push(<span key={`s${i}`} className="text-green-400">{match[2]}</span>);
    } else if (match[3] !== undefined) {
      // Number
      tokens.push(<span key={`n${i}`} className="text-amber-400">{match[3]}</span>);
    } else if (match[4] !== undefined) {
      // Boolean
      tokens.push(<span key={`b${i}`} className="text-sky-400">{match[4]}</span>);
    } else if (match[5] !== undefined) {
      // Null
      tokens.push(<span key={`u${i}`} className="text-red-400">{match[5]}</span>);
    } else if (match[6] !== undefined) {
      // Brackets/braces
      tokens.push(<span key={`br${i}`} className="text-text-lighter">{match[6]}</span>);
    } else if (match[7] !== undefined) {
      // Punctuation (colon, comma)
      tokens.push(<span key={`p${i}`} className="text-text-lighter">{match[7]}</span>);
    } else if (match[8] !== undefined) {
      // Whitespace
      tokens.push(match[8]);
    }
  }

  // Remaining text
  if (lastIndex < json.length) {
    tokens.push(json.slice(lastIndex));
  }

  return tokens;
}

function UploadScreen({
  state,
  onUpload,
  onLoadUrls,
}: {
  state: { status: 'empty' } | { status: 'error'; message: string };
  onUpload: (arch: File, schema?: File) => void;
  onLoadUrls: (archUrl: string, schemaUrl: string | null) => void;
}) {
  const [archFile, setArchFile] = useState<File | null>(null);
  const [schemaFile, setSchemaFile] = useState<File | null>(null);
  const [archUrl, setArchUrl] = useState('');
  const [schemaUrl, setSchemaUrl] = useState('');
  const [mode, setMode] = useState<'upload' | 'url'>('upload');
  const archInputRef = useRef<HTMLInputElement>(null);
  const schemaInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    for (const f of files) {
      const name = f.name.toLowerCase();
      if (name.includes('archigraph') || (name.endsWith('.yaml') && !name.includes('schema'))) {
        setArchFile(f);
      } else if (name.includes('schema')) {
        setSchemaFile(f);
      } else if (!archFile) {
        setArchFile(f);
      }
    }
  }, [archFile]);

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-3.5rem)] px-4">
      <div className="max-w-lg w-full">
        <h1 className="text-2xl font-medium text-text mb-2">ArchiGraph Viewer</h1>
        <p className="text-sm text-text-light mb-6">
          Visualize any ArchiGraph architecture. Load files from URLs or upload them from your computer.
        </p>

        {state.status === 'error' && (
          <div className="bg-error-light border border-error/30 text-error text-sm rounded-lg p-3 mb-4">
            {state.message}
          </div>
        )}

        {/* Mode tabs */}
        <div className="flex gap-0 mb-4 border-b border-border">
          <button
            onClick={() => setMode('upload')}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${mode === 'upload' ? 'border-primary text-primary' : 'border-transparent text-text-lighter hover:text-text-light'}`}
          >
            Upload Files
          </button>
          <button
            onClick={() => setMode('url')}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${mode === 'url' ? 'border-primary text-primary' : 'border-transparent text-text-lighter hover:text-text-light'}`}
          >
            From URL
          </button>
        </div>

        {mode === 'upload' ? (
          <>
            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className="border-2 border-dashed border-border-light rounded-lg p-8 text-center hover:border-primary/50 transition-colors mb-4"
            >
              <p className="text-sm text-text-light mb-3">Drag & drop your YAML files here</p>
              <p className="text-xs text-text-lighter">or select them below</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-text-lighter uppercase tracking-wider">archigraph.yaml *</label>
                <div className="flex gap-2 mt-1">
                  <input ref={archInputRef} type="file" accept=".yaml,.yml" className="hidden" onChange={(e) => setArchFile(e.target.files?.[0] || null)} />
                  <button
                    onClick={() => archInputRef.current?.click()}
                    className="flex-1 text-left text-sm px-3 py-2 bg-bg-elevated border border-border rounded hover:border-border-light transition-colors truncate"
                  >
                    {archFile ? archFile.name : 'Choose file...'}
                  </button>
                  {archFile && (
                    <button onClick={() => setArchFile(null)} className="text-text-lighter hover:text-error px-2">✕</button>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs text-text-lighter uppercase tracking-wider">schema.yaml <span className="normal-case">(optional)</span></label>
                <div className="flex gap-2 mt-1">
                  <input ref={schemaInputRef} type="file" accept=".yaml,.yml" className="hidden" onChange={(e) => setSchemaFile(e.target.files?.[0] || null)} />
                  <button
                    onClick={() => schemaInputRef.current?.click()}
                    className="flex-1 text-left text-sm px-3 py-2 bg-bg-elevated border border-border rounded hover:border-border-light transition-colors truncate"
                  >
                    {schemaFile ? schemaFile.name : 'Choose file...'}
                  </button>
                  {schemaFile && (
                    <button onClick={() => setSchemaFile(null)} className="text-text-lighter hover:text-error px-2">✕</button>
                  )}
                </div>
              </div>

              <button
                onClick={() => archFile && onUpload(archFile, schemaFile || undefined)}
                disabled={!archFile}
                className="w-full py-2.5 bg-primary text-bg font-medium rounded hover:bg-primary-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
              >
                View Architecture
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-text-lighter uppercase tracking-wider">archigraph.yaml URL *</label>
              <input
                type="url"
                value={archUrl}
                onChange={(e) => setArchUrl(e.target.value)}
                placeholder="https://example.com/archigraph.yaml"
                className="w-full mt-1 text-sm px-3 py-2 bg-bg-elevated border border-border rounded focus:border-primary focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="text-xs text-text-lighter uppercase tracking-wider">schema.yaml URL <span className="normal-case">(optional)</span></label>
              <input
                type="url"
                value={schemaUrl}
                onChange={(e) => setSchemaUrl(e.target.value)}
                placeholder="https://example.com/schema.yaml"
                className="w-full mt-1 text-sm px-3 py-2 bg-bg-elevated border border-border rounded focus:border-primary focus:outline-none transition-colors"
              />
            </div>
            <button
              onClick={() => archUrl && onLoadUrls(archUrl, schemaUrl || null)}
              disabled={!archUrl}
              className="w-full py-2.5 bg-primary text-bg font-medium rounded hover:bg-primary-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
            >
              Load & View
            </button>
          </div>
        )}

        <p className="text-xs text-text-lighter mt-4 text-center">
          Files are processed entirely in your browser. Nothing is uploaded to any server.
        </p>
      </div>
    </div>
  );
}
